import React, { ChangeEventHandler, useMemo, useState } from 'react'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import {
  useLabels,
  useLabelCategories,
} from 'src/pages/AccountSettings/AccountSettings.hooks'
import { FilterName } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { NONE_VALUE } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import {
  DropdownItemType,
  NestedFilterType,
} from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'
import {
  ModalForm,
  ModalFormId,
  ModalFormProps,
} from '../../../../../components/ModalForms/ModalForm'
import { Spinner } from '../../../../../Throbber'
import { ListItemPlaceholder } from '../../../../../components/Placeholders/ListItemPlaceholder'
import { ControlsIndexGridNoHeaders } from './ControlsIndexGridNoHeaders'

import { LocalLabelFilterDropdown } from './LocalLabelFilterDropdown'

type MultiSelectListModalProps = Omit<
  ModalFormProps,
  'show' | 'formId' | 'onBack' | 'hide' | 'footerContent'
> & {
  options: (Control | string)[] | undefined
  modalFormId: ModalFormId
  onSubmit: (selected: string[]) => Promise<void>
  formFallback: JSX.Element
}

export const MultiSelectListModal = (
  props: MultiSelectListModalProps,
): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false)
  const [selected, setSelected] = useState<string[]>([])
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([])
  const { data: labelsData, isLoading: isLabelsLoading } = useLabels()
  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useLabelCategories()

  const hide = useHideModal({
    onHide: () => {
      setSelected([])
      setSelectedLabelIds([])
    },
    modalId: props.modalFormId,
  })
  const show = useIsShowModal(props.modalFormId)
  const onBack = useSetActiveModal(ModalFormId.ADD_CONTROL_OPTIONS)
  const onChange: ChangeEventHandler<HTMLInputElement> = ({
    currentTarget: { checked, value },
  }) =>
    setSelected((prevState) =>
      checked ? [...prevState, value] : prevState.filter((p) => p !== value),
    )

  const labels = useMemo(() => labelsData?.getItemsList() || [], [labelsData])
  const categories = useMemo(
    () => categoriesData?.getItemsList() || [],
    [categoriesData],
  )

  const labelFilterValues = useMemo(() => {
    const categoriesMap: Record<string, string> = {}
    categories.forEach((cat) => {
      categoriesMap[cat.getId()] = cat.getName()
    })

    const uncategorizedLabels: DropdownItemType[] = []
    const categoryGroups: Record<string, DropdownItemType[]> = {}

    labels.forEach((label) => {
      const catId = label.getCategoryId()
      const catName = catId ? categoriesMap[catId] : undefined
      const item: DropdownItemType = {
        value: label.getId(),
        label: label.getName(),
      }

      if (catName) {
        if (!categoryGroups[catName]) {
          categoryGroups[catName] = []
        }
        categoryGroups[catName].push(item)
      } else {
        uncategorizedLabels.push(item)
      }
    })

    const items: DropdownItemType[] = [NONE_VALUE]

    Object.entries(categoryGroups).forEach(([catName, groupLabels]) => {
      const nestedFilter: NestedFilterType = {
        filterParam: catName,
        filterValues: groupLabels,
      }
      items.push({
        value: catName,
        label: catName,
        nestedFilter,
      })
    })

    if (uncategorizedLabels.length > 0) {
      items.push({
        value: 'Uncategorized',
        label: 'Uncategorized',
        nestedFilter: {
          filterParam: 'Uncategorized',
          filterValues: uncategorizedLabels,
        },
      })
    }

    return items
  }, [labels, categories])

  const controlObjects = useMemo(() => {
    if (!props.options) return undefined
    return props.options.map((opt) =>
      typeof opt === 'string' ? undefined : opt,
    )
  }, [props.options])

  const filteredControlIds = useMemo(() => {
    if (!props.options) return undefined
    const ids = props.options.map((opt) =>
      typeof opt === 'string' ? opt : opt.getId(),
    )

    if (!selectedLabelIds.length) {
      return ids
    }

    const includeNoLabel = selectedLabelIds.includes(NONE_VALUE.value)
    const labelSet = new Set(
      selectedLabelIds.filter((id) => id !== NONE_VALUE.value),
    )

    return ids.filter((id, index) => {
      const ctrl = controlObjects?.[index]
      if (!ctrl) return false
      const ctrlLabelIds = ctrl.getLabelIdsList()
      const hasNoLabel = !ctrlLabelIds || ctrlLabelIds.length === 0

      if (includeNoLabel && hasNoLabel) {
        return true
      }

      if (labelSet.size > 0 && ctrlLabelIds) {
        return ctrlLabelIds.some((lId) => labelSet.has(lId))
      }

      return false
    })
  }, [props.options, controlObjects, selectedLabelIds])

  const showPlaceholder = props.options === undefined
  const showFallback =
    props.options !== undefined && props.options.length === 0 && !loading

  return (
    <ModalForm
      {...props}
      hide={hide}
      show={show}
      formId={props.modalFormId}
      customSubmitButton={showFallback ? <></> : undefined}
      size="lg"
      onBack={onBack}
      enforceFocus={false}
      showOverflow={true}
    >
      <form
        id={props.modalFormId}
        style={{ minHeight: '320px', display: 'flex', flexDirection: 'column' }}
        onSubmit={async (e) => {
          e.preventDefault()
          setLoading(true)
          await props.onSubmit(selected)
          setLoading(false)
          hide()
        }}
      >
        {!loading &&
          props.options !== undefined &&
          props.options.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <LocalLabelFilterDropdown
                filterName={FilterName.LABEL}
                filterValues={labelFilterValues}
                isLoading={isLabelsLoading || isCategoriesLoading}
                selectedLabelIds={selectedLabelIds}
                onChangeSelectedLabels={setSelectedLabelIds}
              />
            </div>
          )}
        {loading && <Spinner size="l" color="primary" />}
        {showPlaceholder &&
          Array.from({ length: 5 }).map((_, i) => (
            <ListItemPlaceholder key={`${i}`} height={'36'} />
          ))}
        {showFallback && props.formFallback}
        {!loading &&
          filteredControlIds !== undefined &&
          filteredControlIds.length > 0 && (
            <ControlsIndexGridNoHeaders
              controlIds={filteredControlIds}
              selected={selected}
              onChange={onChange}
            />
          )}
        {!loading &&
          filteredControlIds !== undefined &&
          filteredControlIds.length === 0 &&
          props.options !== undefined &&
          props.options.length > 0 && (
            <p style={{ marginTop: '16px', color: '#6c757d' }}>
              No controls match the selected label filter.
            </p>
          )}
      </form>
    </ModalForm>
  )
}
